import { useState } from 'react';
import styled from 'styled-components';
import { StarOutlineIc } from '@dsch/dd-icons';

import { BaseModal } from 'components/Toolkit/Modal/BaseModal';
import { InfoTitle } from 'components/Toolkit/InfoTitle/InfoTitle';
import { RadioButton } from 'components/Toolkit/Inputs/RadioButton';
import { Button } from 'components/Toolkit/Button/Button';
import { TextField } from 'components/Toolkit/Inputs/TextField';
import { Label } from 'components/Toolkit/Inputs/Label';
import { Columns } from 'components/Toolkit/Columns/Columns';
import { Loading } from 'components/Toolkit/Loading/Loading';
import { Notification } from 'components/Toolkit/Notification/Notification';

import { NotificationFrequency } from 'components/SearchPage/hooks/useSavedSearch';

export interface ISaveSearchModalProps {
  isOpen: boolean;
  askToClose: () => void;
  onSave: (notifications: NotificationFrequency, name: string) => void;
  name: string;
  terms: string;
  saving: boolean;
  cdnUrl: string;
  isInstantAlertAvailable: boolean;
}

const Icon = styled(StarOutlineIc)`
  color: ${({ theme }) => theme.colors.ORANGE};
`;

const SInfoTitle = styled(InfoTitle)`
  margin-bottom: ${({ theme }) => theme.spacing.M16};
`;

const Panel = styled.div<{ isInstantAlertAvailable: boolean }>`
  display: block;
  padding: ${({ theme }) => theme.spacing.M16};
  margin-bottom: ${({ isInstantAlertAvailable, theme }) =>
    isInstantAlertAvailable ? theme.spacing.M24 : theme.spacing.M16};
  background: ${({ theme }) => theme.colors.GREY_LIGHTER};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  color: ${({ theme }) => theme.colors.GREY_DARK};
`;
const PanelTitle = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.M16};
  ${({ theme }) => theme.fontSize.M14};
`;

const Option = styled(RadioButton)`
  margin-bottom: ${({ theme }) => theme.spacing.M16};

  label {
    ${({ theme }) => theme.fontSize.M14};
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const Terms = styled.p`
  margin-bottom: ${({ theme }) => theme.spacing.M24};
  ${({ theme }) => theme.fontSize.M14};
`;

const SLabel = styled(Label)`
  margin-bottom: ${({ theme }) => theme.spacing.S8};
  ${({ theme }) => theme.fontSize.M16};
  font-weight: bold;
  color: ${({ theme }) => theme.colors.GREY_DARKER};
`;

const STextField = styled(TextField)`
  margin-bottom: ${({ theme }) => theme.spacing.S8};
`;

const SLoading = styled(Loading)`
  width: ${({ theme }) => theme.spacing.M16};
  height: ${({ theme }) => theme.spacing.M16};
  margin-right: ${({ theme }) => theme.spacing.S8};
`;

const SNotification = styled(Notification)`
  background-color: ${({ theme }) => theme.colors.WHITE};
  margin-bottom: ${({ theme }) => theme.spacing.M24};
  padding: 0;
`;

function SaveSearchModal({
  isOpen,
  askToClose,
  onSave,
  name: initialName,
  terms,
  saving,
  cdnUrl,
  isInstantAlertAvailable,
}: ISaveSearchModalProps) {
  const [selected, setSelected] = useState<NotificationFrequency>(
    NotificationFrequency.DAILY,
  );
  const [name, setName] = useState(initialName);

  return (
    <BaseModal isOpen={isOpen} askToClose={askToClose} showCloseIcon={false}>
      <SInfoTitle
        title="Save this search"
        icon={<Icon width={40} height={40} />}
      />
      <SLabel htmlFor="save-search-name">Name your search</SLabel>
      <STextField
        id="save-search-name"
        value={name}
        willUseSubText={false}
        onChange={setName}
      />
      <Terms data-testid="save-search-terms">{terms}</Terms>
      <Panel isInstantAlertAvailable={isInstantAlertAvailable}>
        <PanelTitle>Want to get notified on this search?</PanelTitle>
        <Option
          name="notifications"
          value="DAILY"
          label="Yes, daily alerts"
          checked={selected === 'DAILY'}
          onChange={() => setSelected(NotificationFrequency.DAILY)}
        />
        {isInstantAlertAvailable ? (
          <Option
            name="notifications"
            value="INSTANT"
            label="Yes, instant alerts"
            checked={selected === 'INSTANT'}
            onChange={() => setSelected(NotificationFrequency.INSTANT)}
          />
        ) : null}
        <Option
          name="notifications"
          value="OFF"
          label="No alerts"
          checked={selected === 'OFF'}
          onChange={() => setSelected(NotificationFrequency.OFF)}
        />
      </Panel>

      {!isInstantAlertAvailable ? (
        <SNotification
          type="INFO"
          text="Instant alerts are only available for more refined searches"
          gapVariant="SMALL"
          size="SMALL"
        />
      ) : null}

      <Columns>
        <Button ofType="SECONDARY" onClick={askToClose}>
          Cancel
        </Button>
        {saving ? (
          <Button
            ofType="PRIMARY"
            icon={<SLoading color="LIGHT" cdnUrl={cdnUrl} />}
          >
            Saving
          </Button>
        ) : (
          <Button
            data-testid="save-seach-button"
            ofType="PRIMARY"
            onClick={() => onSave(selected, name)}
          >
            Save Search
          </Button>
        )}
      </Columns>
    </BaseModal>
  );
}

export { SaveSearchModal };
